// corpusApi — fetches the read-only well corpus that `blueprints/wells.py` serves (PB-04).
//
// CHARTER NOTE: fetch + boundary shape-check only. No Engineering / Economic / Statistical math —
// every value is transcribed by the Python writers, which is where that math is allowed to live.
//
// WHY NOT RTK QUERY. `wellApi.ts`/`padApi.ts` already declare these endpoints and stay the contract
// of record for the app's component layer. The SPINE, though, is a `useSyncExternalStore` module
// store that deliberately needs no Provider — `spineStore.ts` says so explicitly, so that the
// spine's store-free test suites keep working. Routing its hydration through RTK Query would drag a
// Redux Provider into all of them. Same endpoints, same shapes; this is the spine's own reader.
//
// WHAT THAT COSTS, AND HOW IT IS PAID. Staying out of Redux also means staying out of
// `apiSlice.prepareHeaders`, so this path sent NO `Authorization` and no acting-organization —
// an unauthenticated data path to the same routes the authenticated slices use. Nothing caught it
// because the Flask server checks neither today. It is paid by `lib/requestContext`, a
// module-level context the auth layer sets and this reader consumes: the Provider-free design
// survives and the backend still sees one consistent client.

import { authHeaders } from "../../../lib/requestContext";
import type { Pad, Well } from "../types";

/** Vite proxies `/api` to the compute server in dev (vite.config.ts); prod sets VITE_API_URL. */
const API_BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

export class CorpusFetchError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "CorpusFetchError";
  }
}

async function getJson<T>(path: string): Promise<T> {
  let res: Response;
  try {
    // The spine cannot see Redux (see the header note), so auth and acting-org come from the
    // module-level request context rather than `apiSlice.prepareHeaders`. Same headers, same shape
    // — a backend implementing this endpoint sees one client, not two.
    res = await fetch(`${API_BASE}${path}`, { headers: authHeaders() });
  } catch (e) {
    // The corpus needs the Python server. Say so, rather than surfacing a bare "Failed to fetch":
    // demos run with the server up (DECISIONS.md 2026-08-27 — selected from options, an
    // agent-written label, not the PO's own words), so this is a setup fault worth naming.
    throw new CorpusFetchError(
      `Cannot reach the corpus API at ${API_BASE}${path}. Is the compute server running? ` +
        `(${(e as Error).message})`,
    );
  }
  if (!res.ok) {
    if (res.status === 503) {
      // PREFER THE SERVER'S OWN MESSAGE. A canned one here named a build that does not create
      // every missing artifact — a corpus predating the planned-curve table needs a different
      // script, and the route says which. Fall back only when the body carries nothing.
      let fromServer: string | null = null;
      try {
        const body = (await res.json()) as { error?: unknown };
        if (typeof body.error === "string" && body.error)
          fromServer = body.error;
      } catch {
        /* not JSON — fall through to the default */
      }
      throw new CorpusFetchError(
        fromServer ??
          "The demo corpus has not been built. Run: " +
            "python3 scripts/build_demo_db.py --wells docs/subcorpus-demo-wells.txt " +
            "--out data/openwells-subcorpus.sqlite",
        503,
      );
    }
    throw new CorpusFetchError(
      `GET ${path} failed (HTTP ${res.status}).`,
      res.status,
    );
  }
  return (await res.json()) as T;
}

/** Boundary check. The spine's invariants are `id` and — for a Well — `wellbores[]`; a payload
 *  missing them would fail far from here, inside a render. */
function assertShape<T extends { id?: unknown }>(
  rows: unknown,
  what: string,
): T[] {
  if (!Array.isArray(rows)) {
    throw new CorpusFetchError(`Corpus ${what} response was not an array.`);
  }
  for (const r of rows) {
    if (
      !r ||
      typeof r !== "object" ||
      typeof (r as { id?: unknown }).id !== "string"
    ) {
      throw new CorpusFetchError(
        `Corpus ${what} contained a record with no string id.`,
      );
    }
  }
  return rows as T[];
}

export async function fetchCorpusPads(): Promise<Pad[]> {
  return assertShape<Pad>(await getJson<unknown>("/field-pads"), "pads");
}

export async function fetchCorpusWells(): Promise<Well[]> {
  const rows = assertShape<Well>(await getJson<unknown>("/wells"), "wells");
  // `wellbores` is non-optional on `Well`; the API omits absent optionals rather than sending
  // null, so normalize the one field consumers index into unconditionally.
  for (const w of rows) {
    if (!Array.isArray(w.wellbores)) w.wellbores = [];
  }
  return rows;
}

export interface CorpusStatus {
  available: boolean;
  path?: string;
  sizeBytes?: number;
  wells?: number;
  wellbores?: number;
  pads?: number;
  tiers?: Record<string, number>;
  readOnly?: boolean;
  build?: string;
}

export async function fetchCorpusStatus(): Promise<CorpusStatus> {
  return getJson<CorpusStatus>("/corpus/status");
}

/** One well's full detail, shaped as the FE's `LoadedWell` so the EXISTING prebake mapper can
 *  consume it unchanged. `complete` is false when a required family is absent, which is the common
 *  case rather than an error: 2,034 of the corpus's 2,538 wells sit in the `daily` tier. That tier
 *  names an incomplete full-5 COMBINATION, not an empty well — measured, 97% of them have hole
 *  sections and 73% have casing, and only 10 of 2,538 are genuinely daily-and-nothing-else. `bundle` is left `unknown` here: it is validated by the prebake Zod schemas at the point
 *  of use, which is where that vocabulary already lives. */
/** One campaign as the pad-campaign routes serve it. Mirrors `PadJobResource` in
 *  `features/jobs/store/padJobApi.ts`; declared structurally here rather than imported so the
 *  spine's reader keeps no dependency on the jobs feature (the same Provider-free discipline this
 *  file's header describes). */
export interface CorpusPadJob {
  id: string;
  padId: string;
  name: string;
  jobType: string;
  status: string;
  startDate?: string | null;
  endDate?: string | null;
  memberCount: number;
}

/** One member of a campaign. NOT its stimulation stream — that rides the well bundle, and putting
 *  it here would make the member list ~160 KB per well for data the caller may not render. */
export interface CorpusPadJobMember {
  jobId: string;
  jobNumber: string;
  wellId: string;
  wellName: string;
  wellboreId: string;
  startDate?: string | null;
  endDate?: string | null;
}

interface CorpusPage<T> {
  items: T[];
  nextCursor?: string | null;
}

/** WHICH CAMPAIGN CONTAINS THIS JOB — one request, which is the whole point of the route.
 *
 *  Returns null when the job is in no campaign, which is the ORDINARY case: a single-well frac has
 *  no campaign and must not be reported as an error. A corpus that predates the pad-campaign tables
 *  answers 503 and `getJson` throws, so an old corpus is distinguishable from a solo well.
 */
export async function fetchCorpusPadJobForMember(
  jobId: string,
): Promise<CorpusPadJob | null> {
  const page = await getJson<CorpusPage<CorpusPadJob>>(
    `/pad-jobs?memberJobId=${encodeURIComponent(jobId)}`,
  );
  return page.items[0] ?? null;
}

/** A campaign's members, following `nextCursor` to the end.
 *
 *  PAGES RATHER THAN ASSUMING ONE RESPONSE. A zipper is two or three wells, but that is an
 *  expectation and not a bound — a batch drill has no written ceiling — and a reader that ignores
 *  the cursor silently truncates the campaign at the server's page size. The guard bounds the loop
 *  so a server that always returns a cursor cannot spin the tab.
 */
export async function fetchCorpusPadJobMembers(
  padJobId: string,
): Promise<CorpusPadJobMember[]> {
  const out: CorpusPadJobMember[] = [];
  let cursor: string | undefined;
  for (let guard = 0; guard < 50; guard += 1) {
    const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
    const page = await getJson<CorpusPage<CorpusPadJobMember>>(
      `/pad-jobs/${encodeURIComponent(padJobId)}/members${qs}`,
    );
    out.push(...page.items);
    if (!page.nextCursor) return out;
    cursor = page.nextCursor;
  }
  return out;
}

export interface CorpusWellBundle {
  wellId: string;
  tier: string | null;
  complete: boolean;
  missing: string[];
  failed: Record<string, string>;
  bundle: unknown;
}

export async function fetchCorpusWellBundle(
  wellId: string,
): Promise<CorpusWellBundle> {
  return getJson<CorpusWellBundle>(
    `/wells/${encodeURIComponent(wellId)}/bundle`,
  );
}

/** One version of a well's planned days-vs-depth curve, as `/planned-curves` returns it. */
export interface CorpusPlannedCurveVersion {
  versionNo: number;
  isCurrent: boolean;
  set: unknown; // validated by migratePlannedCurveSet at the point of use
}

export interface CorpusPlannedCurves {
  wellId: string;
  /** One PAGE of versions, newest first. `nextCursor` is opaque — pass it back, do not parse it. */
  items: CorpusPlannedCurveVersion[];
  /** The pre-paging name, still sent for back-compat. Readers should use `items`. */
  sets?: CorpusPlannedCurveVersion[];
  nextCursor: string | null;
  sortOrder: string;
  currentSetId: string | null;
}

/** A well's planned curve VERSIONS, newest first (CURVE-4, DECISIONS.md 2026-09-04).
 *
 *  The demo's plans are built in the database rather than shipped as files, and a user's CSV
 *  import lands here too — "as a version of the DVD or CVD". So this returns a collection, and a
 *  well with none returns an empty one rather than an error: no plan is the ordinary case. */
export async function fetchCorpusPlannedCurves(
  wellId: string,
  cursor?: string,
): Promise<CorpusPlannedCurves> {
  const q = cursor ? `?cursor=${encodeURIComponent(cursor)}` : "";
  return getJson<CorpusPlannedCurves>(
    `/wells/${encodeURIComponent(wellId)}/planned-curves${q}`,
  );
}
